import * as React from "react";
import { RouteComponentProps } from "react-router";
import { Link } from "react-router-dom";
import { ConstColor } from "src/config/styles_const/styles_const";
import { createContractCustomerReportAPI } from "src/infra/api/service/contract_customer_report_api";
import {
  toDateString,
  toYMString,
  ymToMonth,
  ymToYear,
} from "src/util/commonFuntion";
import styled from "styled-components";
import { routeConfig } from "../../config/language/ja-JP/presentation/route_text";
import {
  AppNotificationMessage,
  AppNotificationType,
} from "../../domain/model/app_notification_message";
import { FrontDate } from "../../domain/model/frontDate";
import { IContractRepository } from "../../domain/repository/contract_repository";
import { createApiClient, IApiClient } from "../../infra/api/client";
import { entity } from "../../infra/api/rpc/api";
import { createContractAPI } from "../../infra/api/service/contract_api";
import PageWrapper from "../common/atom/wrapper/page_wrapper";
import Breadcrumbs from "../common/molecular/breadcrumbs/breadcrumbs";
import AppNotificationWithTexts from "../common/organism/app_notification_with_texts";
import Progress from "../common/organism/progress";
import { TBreadcrumbItem } from "../type/common";

const apiClient: IApiClient = createApiClient();
const contractRepository: IContractRepository = createContractAPI(apiClient);
const reportRepository = createContractCustomerReportAPI(apiClient);

interface IProps
  extends RouteComponentProps<{
    contractId: string;
    reportId: string;
  }> {}

const CustomerContractOperationReportDetail: React.FC<IProps> = (props) => {
  const [contract, setContract] = React.useState<
    entity.IContract | undefined
  >();
  const [report, setReport] = React.useState<
    entity.IContractCustomerReport | undefined
  >();
  const [messages, setMessages] = React.useState<AppNotificationMessage[]>([]);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  React.useEffect(() => {
    try {
      contractRepository
        .getOperation(props.match.params.contractId)
        .then((res) => {
          setContract(res);
        });
      reportRepository.get(props.match.params.reportId).then((res) => {
        setReport(res);
      });
    } catch (e) {
      setMessages([
        AppNotificationMessage.from(
          "",
          AppNotificationType.ERROR,
          "エラーが発生しました",
          FrontDate.fromEmpty()
        ),
      ]);
    }
  }, []);

  const breadcrumbItems: TBreadcrumbItem[] = [
    routeConfig.customerRoot,
    { text: `稼働報告` },
  ];

  if (!contract || !report || !contract.operation) {
    return <Progress canAnimation={false} />;
  }

  return (
    <React.Fragment>
      {messages.length > 0 && (
        <AppNotificationWithTexts
          onClose={(id: string) =>
            (event: any): void => {
              setMessages([]);
            }}
          appNotificationMessages={messages}
        />
      )}

      {isLoading && <Progress canAnimation={false} />}
      <PageWrapper>
        <Breadcrumbs breadcrumbItems={breadcrumbItems} />

        <PageSubTitle>稼働報告ページ</PageSubTitle>
        <PageTitle>{toYMString(report.targetYM || "")}</PageTitle>

        <ItemLabel>対象の成約</ItemLabel>
        <Item>
          <ItemSection>
            {contract.operation.status ===
              entity.ContractOperation.Status.Status_InProgress && (
              <InProgressLabel>稼働中</InProgressLabel>
            )}
            {contract.operation.status ===
              entity.ContractOperation.Status.Status_Completed && (
              <EndLabel>稼働終了</EndLabel>
            )}
            <ProjectNameLabel
              target="_blank"
              to={`/customer/project/${contract.projectID}`}
            >
              {contract.projectName}
            </ProjectNameLabel>
          </ItemSection>

          <DateContainer>
            <DateLabel>
              稼働開始日: {toDateString(contract.operation!!.startedDate || "")}
            </DateLabel>

            {contract.operation.status ===
              entity.ContractOperation.Status.Status_InProgress && (
              <DateLabel>
                稼働終了予定:{" "}
                {toYMString(contract.operation.endScheduledYM || "")}
              </DateLabel>
            )}
            {contract.operation.status ===
              entity.ContractOperation.Status.Status_Completed && (
              <DateLabel>
                稼働終了日: {toDateString(contract.operation.endedDate || "")}
              </DateLabel>
            )}
          </DateContainer>

          <ItemSection>
            <CompanyInfo>
              <CompanyName>{contract.companyName}</CompanyName>
            </CompanyInfo>
          </ItemSection>
        </Item>

        <ItemLabel>稼働報告</ItemLabel>

        <FormWrapper>
          <InputSection>
            先月の稼働内容<span style={{ color: "red" }}>*</span>
          </InputSection>
          <TextArea
            placeholder="例 )2021年の春にリリースした新製品、〇〇○のグロースハックを目的に、プロダクトマネジメント業務を依頼。オンボーディングからアクティベーションプロセスの改善設計を中心に、IT部門と連携し、改善作業を行う。また社内でのプロダクトチームの組織体制を見直してもらう。"
            value={report.detail || ""}
            disabled={true}
          />

          <div
            style={{
              display: "flex",
              alignItems: "top",
              marginTop: "24px",
              marginBottom: "24px",
            }}
          >
            <div style={{ width: "250px" }}>
              <InputSection>
                稼働内容への満足度<span style={{ color: "red" }}>*</span>
              </InputSection>
              <div style={{ display: "flex", alignItems: "end" }}>
                <div style={{ width: "250px", marginRight: "10px" }}>
                  <Select value={report.satisfactionLevel || 0} disabled={true}>
                    <option
                      value={
                        entity.ContractOperationReport.SatisfactionLevel
                          .SatisfactionLevel_Unknown
                      }
                    >
                      ~~
                    </option>
                    <option
                      value={
                        entity.ContractOperationReport.SatisfactionLevel
                          .SatisfactionLevel_Good
                      }
                    >
                      満足している
                    </option>
                    <option
                      value={
                        entity.ContractOperationReport.SatisfactionLevel
                          .SatisfactionLevel_Normal
                      }
                    >
                      普通
                    </option>
                    <option
                      value={
                        entity.ContractOperationReport.SatisfactionLevel
                          .SatisfactionLevel_Bad
                      }
                    >
                      満足していない
                    </option>
                  </Select>
                </div>
              </div>
            </div>

            <div style={{ width: "250px", marginLeft: "30px" }}>
              <InputSection>
                契約の継続に関して<span style={{ color: "red" }}>*</span>
              </InputSection>
              <div style={{ display: "flex", alignItems: "end" }}>
                <div style={{ width: "250px", marginRight: "10px" }}>
                  <Select value={report.continuationLevel || 0} disabled={true}>
                    <option
                      value={
                        entity.ContractOperationReport.ContinuationLevel
                          .ContinuationLevel_Unknown
                      }
                    >
                      ~~
                    </option>
                    <option
                      value={
                        entity.ContractOperationReport.ContinuationLevel
                          .ContinuationLevel_Continue
                      }
                    >
                      継続予定
                    </option>
                    <option
                      value={
                        entity.ContractOperationReport.ContinuationLevel
                          .ContinuationLevel_Consider
                      }
                    >
                      継続検討中
                    </option>
                    <option
                      value={
                        entity.ContractOperationReport.ContinuationLevel
                          .ContinuationLevel_Stop
                      }
                    >
                      終了予定
                    </option>
                  </Select>
                </div>
              </div>
            </div>

            <div style={{ width: "300px", marginLeft: "30px" }}>
              <InputSection>契約終了予定</InputSection>
              <div style={{ display: "flex", alignItems: "end" }}>
                <div style={{ width: "100px", marginRight: "10px" }}>
                  <Input
                    placeholder="2022"
                    fromRight={true}
                    value={ymToYear(contract.operation.endScheduledYM || "")}
                    disabled={true}
                  />
                </div>
                <div style={{ fontSize: "12px" }}>年</div>
                <div
                  style={{
                    width: "100px",
                    marginRight: "10px",
                    marginLeft: "16px",
                  }}
                >
                  <Input
                    placeholder="04"
                    fromRight={true}
                    value={ymToMonth(contract.operation.endScheduledYM || "")}
                    disabled={true}
                  />
                </div>
                <div style={{ fontSize: "12px" }}>月</div>
              </div>
            </div>
          </div>

          <InputSection>サポート依頼事項</InputSection>
          <MiniTextArea
            placeholder="例）ミーティングでの意思疎通が行いずらく相談に乗ってほしい"
            value={report.supportRequest || ""}
            disabled={true}
          />

          <InputSection>
            本成約とは別でご支援が必要な業務課題などがございましたらこちらのフォームよりお知らせください
          </InputSection>
          <MiniTextArea
            placeholder="例）エンジニアの採用を強化したい"
            value={report.otherRequest || ""}
            disabled={true}
          />
        </FormWrapper>
      </PageWrapper>
    </React.Fragment>
  );
};

export default CustomerContractOperationReportDetail;

const PageSubTitle = styled.div`
  font-size: 15px;
  font-weight: bold;
  color: #333333;
  margin-top: 30px;
`;

const PageTitle = styled.div`
  font-size: 26px;
  font-weight: bold;
  color: #333333;
  margin-top: 10px;
  margin-bottom: 30px;
`;

const ItemLabel = styled.div`
  font-size: 13px;
  font-weight: bold;
  color: #333333;
  margin-bottom: 12px;
`;

const Item = styled.div`
  padding: 5px 5px;
  width: 100%;
  border-bottom: 1px solid ${ConstColor.PlaceholderAndBorder};
  margin-bottom: 30px;
`;

const ItemSection = styled.div`
  display: flex;
  align-items: center;
  padding: 5px 0px;
`;

const EndLabel = styled.div`
  margin-right: 10px;
  background: #a4a4a4;
  border-radius: 4px;
  padding: 4px 15px;
  font-size: 9px;
  font-weight: bold;
  color: white;
`;

const InProgressLabel = styled.div`
  margin-right: 10px;
  background: ${ConstColor.Success};
  border-radius: 4px;
  padding: 4px 15px;
  font-size: 9px;
  font-weight: bold;
  color: white;
`;

const DateContainer = styled.div`
  display: flex;
  margin-top: 8px;
  margin-bottom: 8px;
`;

const DateLabel = styled.div`
  font-size: 11px;
  color: #333333;
  margin-right: 45px;
`;

const ProjectNameLabel = styled(Link)`
  font-size: 11px;
  font-weight: bold;
  color: #333333;
  margin-right: 7px;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  ::after {
    font-family: "Font Awesome 5 Free";
    content: "\f35d";
    margin-left: 5px;
  }
`;

const CompanyInfo = styled.div`
  flex-grow: 1;
  padding: 0px 10px;
`;

const CompanyName = styled.div`
  font-size: 15px;
  font-weight: bold;
  color: #333333;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 24px;
`;

const FormWrapper = styled.div`
  width: 100%;
  margin-bottom: 40px;
  padding: 30px 20px;
  background-color: rgba(241, 246, 248, 0.5);
`;

const InputSection = styled.div`
  margin-top: 20px;
  font-size: 12px;
`;

const Input = styled.input<{ fromRight: boolean }>`
  font-size: 12px;
  margin-top: 8px;
  height: 50px;
  border: none;
  border: solid 1px #a4a4a4;
  border-radius: 3px;
  width: 100%;
  padding: 0px 5px;
  text-align: ${({ fromRight }) => (fromRight ? "right" : "left")};
`;

const TextArea = styled.textarea`
  font-size: 12px;
  margin-top: 8px;
  height: 50px;
  border: none;
  border: solid 1px #a4a4a4;
  border-radius: 3px;
  width: 100%;
  height: 150px;
  padding: 5px;
`;

const MiniTextArea = styled.textarea`
  font-size: 12px;
  margin-top: 8px;
  height: 50px;
  border: none;
  border: solid 1px #a4a4a4;
  border-radius: 3px;
  width: 100%;
  height: 80px;
  padding: 5px;
`;

const Select = styled.select`
  font-size: 12px;
  margin-top: 8px;
  height: 50px;
  border: none;
  border: solid 1px #a4a4a4;
  border-radius: 3px;
  width: 100%;
  padding: 0px 5px;
  color: #000;
`;



// WEBPACK FOOTER //
// ./src/presentation/container/customer_contract_operation_report_detail.tsx